import type { CSSProperties } from 'react';
import { uebergangsMaske, type TransitionKind } from '@schnitt/shared';

/**
 * **Die Form eines Übergangs im Weg ohne Leinwand** (7.161).
 *
 * Die Leinwand malt die Form im Shader (7.147/7.148). Der Weg mit Elementen
 * blendete bis zum 24. September bei jeder Art einfach über -- und das ist
 * der Weg, den die Vorschau als „Wie bisher“ voreingestellt zeigt. Wer eine
 * Wischblende setzte, sah dort eine Auflösung.
 *
 * Hier wird dieselbe Maske (`uebergangsMaske` aus dem gemeinsamen Teil) als
 * CSS-Maske auf das Videoelement gelegt:
 * - die vier harten Wischer als Verlauf mit harter Kante -- genau, und die
 *   Uhr als Kegelverlauf;
 * - die weichen als kleines Bild aus derselben Rechnung, das der Browser
 *   hochskaliert. Ihre Rampen sind breit, das Hochskalieren sieht man nicht.
 *
 * Gerechnet wird **in Leinwand-Koordinaten**, wie im Shader und im Film.
 * Das Element ist aber nur so gross wie sein Bild (bei einem Querformat im
 * Hochkant kleiner), also wird die Maske auf die Leinwand gelegt und nicht
 * auf das Element: `mask-size` und `mask-position` rechnen die Leinwand in
 * die Koordinaten des Elements um. Eine Drehung des Clips bleibt dabei
 * aussen vor -- mitten in einem Übergang ist sie selten.
 */

/** Wie fein das Bild der weichen Formen ist, auf der langen Seite. */
const FEIN = 96;
/** Der Fortschritt wird in so vielen Stufen gerechnet und gemerkt. */
const STUFEN = 200;

const gemerkt = new Map<string, string>();

function verlauf(art: TransitionKind, f: number, umgekehrt: boolean, seiten: number): string | null {
  const [an, aus] = umgekehrt ? ['transparent', '#000'] : ['#000', 'transparent'];
  const pz = (v: number) => `${(v * 100).toFixed(3)}%`;
  switch (art) {
    case 'wipeleft':
      return `linear-gradient(to right, ${aus} ${pz(1 - f)}, ${an} ${pz(1 - f)})`;
    case 'wiperight':
      return `linear-gradient(to right, ${an} ${pz(f)}, ${aus} ${pz(f)})`;
    case 'wipeup':
      return `linear-gradient(to bottom, ${aus} ${pz(1 - f)}, ${an} ${pz(1 - f)})`;
    case 'wipedown':
      return `linear-gradient(to bottom, ${an} ${pz(f)}, ${aus} ${pz(f)})`;
    case 'radial': {
      // **Die Uhr als Kegelverlauf.** Aus einem kleinen Bild hochskaliert
      // verschmierte ihr Schnitt senkrecht über der Mitte über mehrere
      // Punkte (37 von 255). Ein Kegelverlauf misst seinen Winkel wie der
      // Shader in Bildpunkten.
      // Gefragt wird die Maske selbst, an einem Punkt auf einem Kreis um die
      // Mitte -- so steht die Rechnung weiter an einer Stelle.
      const stufen: string[] = [];
      for (let grad = 0; grad <= 360; grad += 2) {
        const phi = (grad * Math.PI) / 180;
        const m = uebergangsMaske(art, f, 0.5 + (Math.sin(phi) * 0.25) / seiten, 0.5 - Math.cos(phi) * 0.25, seiten);
        const a = umgekehrt ? 1 - m : m;
        stufen.push(`rgba(0,0,0,${a.toFixed(4)}) ${grad}deg`);
      }
      return `conic-gradient(${stufen.join(', ')})`;
    }
    default:
      return null;
  }
}

function weich(art: TransitionKind, f: number, umgekehrt: boolean, seiten: number): string | null {
  const nx = seiten >= 1 ? FEIN : Math.max(8, Math.round(FEIN * seiten));
  const ny = seiten >= 1 ? Math.max(8, Math.round(FEIN / seiten)) : FEIN;
  const schluessel = `${art}|${f}|${umgekehrt ? 1 : 0}|${nx}x${ny}`;
  const schon = gemerkt.get(schluessel);
  if (schon) return schon;
  if (typeof document === 'undefined') return null;
  const flaeche = document.createElement('canvas');
  flaeche.width = nx;
  flaeche.height = ny;
  const ctx = flaeche.getContext('2d');
  if (!ctx) return null;
  const bild = ctx.createImageData(nx, ny);
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const m = uebergangsMaske(art, f, (i + 0.5) / nx, (j + 0.5) / ny, seiten);
      const k = (j * nx + i) * 4;
      bild.data[k] = 255;
      bild.data[k + 1] = 255;
      bild.data[k + 2] = 255;
      bild.data[k + 3] = Math.round((umgekehrt ? 1 - m : m) * 255);
    }
  }
  ctx.putImageData(bild, 0, 0);
  const url = `url(${flaeche.toDataURL('image/png')})`;
  // Ein Übergang braucht höchstens STUFEN Bilder je Richtung; mehr als ein
  // paar Übergänge muss niemand gleichzeitig im Kopf haben.
  if (gemerkt.size > 4 * STUFEN) gemerkt.clear();
  gemerkt.set(schluessel, url);
  return url;
}

/**
 * Die CSS-Maske für ein Element in einem Übergang mit Form.
 *
 * `umgekehrt` heisst: das Element ist der *gehende* Clip und liegt oben --
 * dann zeigt es, was der kommende noch nicht zeigt (`1 − m`).
 * `kasten` ist die Grösse des Elements in Prozent der Leinwand, `lage` seine
 * Mitte (Anteile) und Skalierung.
 */
export function uebergangsMaskenStil(
  art: TransitionKind,
  p: number,
  umgekehrt: boolean,
  seiten: number,
  kasten: { w: number; h: number },
  lage: { x: number; y: number; scale: number },
): CSSProperties {
  const f = Math.round(Math.max(0, Math.min(1, p)) * STUFEN) / STUFEN;
  const bild = verlauf(art, f, umgekehrt, seiten) ?? weich(art, f, umgekehrt, seiten);
  if (!bild) return {};
  const s = lage.scale > 0 ? lage.scale : 1;
  // Die Leinwand in Koordinaten des Elements (vor seiner Skalierung): so
  // gross wie sie geteilt durch die Skalierung, und ihr linker Rand so weit
  // links von der linken Kante des Elements, wie die Mitte es verlangt.
  const groesse = `calc(var(--canvas-width, 0px) * ${(1 / s).toFixed(5)}) calc(var(--canvas-height, 0px) * ${(1 / s).toFixed(5)})`;
  const links = (-lage.x / s + kasten.w / 200).toFixed(5);
  const oben = (-lage.y / s + kasten.h / 200).toFixed(5);
  const stelle = `calc(var(--canvas-width, 0px) * ${links}) calc(var(--canvas-height, 0px) * ${oben})`;
  return {
    maskImage: bild,
    WebkitMaskImage: bild,
    maskSize: groesse,
    WebkitMaskSize: groesse,
    maskPosition: stelle,
    WebkitMaskPosition: stelle,
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
  };
}
